import type React from "react";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import { FULFILLMENT_LABELS, FULFILLMENT_VERB, type FulfillmentConflict } from "@/util/fulfillment";

/**
 * "Switch to Nationwide?" — lists the cart items that can't follow and asks
 * before removing them. Native <dialog> (top layer) so it stacks above the
 * quick-add modal; bottom sheet on mobile, centered on desktop.
 */
export const FulfillmentConflictSheet: React.FC<{
  conflict: FulfillmentConflict;
  busy?: boolean;
  onRemoveAndSwitch: () => void;
  onKeep: () => void;
}> = ({ conflict, busy, onRemoveAndSwitch, onKeep }) => {
  const count = conflict.items.length;
  const toLabel = FULFILLMENT_LABELS[conflict.to];
  const fromLabel = FULFILLMENT_LABELS[conflict.from];

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click dismisses; Esc does the same via onCancel
    <dialog
      ref={(el) => {
        if (el && !el.open) el.showModal();
      }}
      onCancel={(e) => {
        e.preventDefault();
        onKeep();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onKeep();
      }}
      aria-labelledby="fulfillment-conflict-title"
      className="w-full max-w-none lg:max-w-md m-0 mt-auto lg:m-auto p-0 bg-transparent backdrop:bg-black/50 backdrop:backdrop-blur-sm open:animate-slide-up lg:open:animate-modal-in"
    >
      <div className="bg-white rounded-t-2xl lg:rounded-2xl shadow-2xl p-5 pb-[52px] lg:pb-5 space-y-4">
        <div className="space-y-1">
          <h2 id="fulfillment-conflict-title" className="text-base font-semibold text-gray-900">
            Switch to {toLabel}?
          </h2>
          <p className="text-sm text-gray-600">
            {count} {count === 1 ? "meal" : "meals"} in your cart can&apos;t{" "}
            {FULFILLMENT_VERB[conflict.to]}:
          </p>
        </div>

        <ul className="space-y-2">
          {conflict.items.map((item) => (
            <li key={item.itemId} className="flex items-center gap-3">
              <div className="relative size-10 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                {item.product.images[0] && (
                  <ResponsiveImage
                    image={item.product.images[0]}
                    objectFit="cover"
                    className="absolute inset-0 w-full h-full"
                  />
                )}
              </div>
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-gray-900 truncate">{item.product.name}</span>
                {item.reason && (
                  <span className="block text-xs text-gray-400 truncate">{item.reason}</span>
                )}
              </span>
              {item.quantity > 1 && (
                <span className="text-xs text-gray-400 tabular-nums">×{item.quantity}</span>
              )}
            </li>
          ))}
        </ul>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onKeep}
            disabled={busy}
            className="flex-1 py-3 rounded-xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Keep {fromLabel}
          </button>
          <button
            type="button"
            onClick={onRemoveAndSwitch}
            disabled={busy}
            className="flex-1 py-3 rounded-xl text-sm font-semibold bg-brand-mint text-gray-900 hover:bg-brand-mint/90 cursor-pointer active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Remove &amp; switch
          </button>
        </div>
      </div>
    </dialog>
  );
};
